import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import logoDesk from "../images/navLogo_Desk.png";
import exitIconM from "../images/exit_Icon.svg";
import heartIcon from "../images/heartIcon.svg";
import arrowUp from "../images/upArrow.svg";
import arrowDown from "../images/downArrow.svg";

/**
 * Navbar Component
 * 
 * A responsive navigation bar with the following features:
 * - Desktop: Fixed/absolute positioning with hover-based dropdowns
 * - Mobile: Bottom-fixed menu with expandable sections
 * - Scroll-aware: Changes appearance when user scrolls past 70px
 * - Performance optimized: Uses useCallback, useMemo, and refs to minimize re-renders
 * 
 * Props:
 * - currentPage: String matching menuItems.page to highlight active page
 * - whichRef: Ref to scroll to specific sections on the page
 */

const Navbar = (props) => {
  // ========================================
  // STATE MANAGEMENT
  // ========================================
  
  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Dropdown state - tracks which dropdown is currently open (index) or null if none
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Language selector state (for future localization feature)
  const [selectedLanguage, setSelectedLanguage] = useState("EN");
  
  // Dynamic height for dropdown animations
  const [dropdownHeight, setDropdownHeight] = useState(0);
  const dropdownRef = useRef(null); // Ref to measure dropdown height
  const hideDropdownTimeout = useRef(null); // Timeout for delayed dropdown hiding
  
  // Hover state - tracks if mouse is over the navbar
  const [isNavHovered, setIsNavHovered] = useState(false);
  
  // Performance optimization: Tracks previous nav state to prevent unnecessary re-renders
  const prevNavStateRef = useRef({ menuTop: null, logoShow: null });
  const navHoverTimeout = useRef(null); // Timeout for delayed hover state changes (600ms)

  // Page-load fade-in: hides desktop nav items until layout has settled to prevent positional "shake"
  const [isNavReady, setIsNavReady] = useState(false);
  const navReadyTimer = useRef(null);

  /**
   * Effect: Notify parent when navbar overlay is active
   * Fires onOverlayActive(true) when desktop dropdown is open OR mobile menu is open
   * Fires onOverlayActive(false) otherwise
   * Parent uses this to toggle z-index on the page logo
   */
  const { onOverlayActive } = props;
  useEffect(() => {
    if (onOverlayActive) {
      const isOverlayActive = activeDropdown !== null || isMobileMenuOpen;
      onOverlayActive(isOverlayActive);
    }
  }, [activeDropdown, isMobileMenuOpen, onOverlayActive]);

  // Dynamic navbar positioning classes
  const [menuTop, setMenuTop] = useState(
    `mx-auto xs:fixed md:absolute w-full z-[22] text-[#b24029] transition-all duration-1000 ease-in-out bg-[#DCB1FA] md:bottom-auto xs:bottom-0 md:transform-gpu`
  );

  // Dynamic logo visibility classes
  const [logoShow, setLogoShow] = useState(`h-10 opacity-0 `);

  // ========================================
  // MENU CONFIGURATION
  // ========================================
  
  /**
   * Menu items configuration
   * Uses useMemo to prevent recreation on every render (performance optimization)
   * Each item can have:
   * - name: Display text
   * - link: URL to navigate to
   * - page: Identifier to match with currentPage prop for active highlighting
   * - dropdown: Array of submenu items with {name, url}
   * - external: Boolean for external links (opens in new tab)
   * - action: Function for special behaviors (e.g., Exit site)
   */
  const menuItems = useMemo(() => [
    {
      name: "Get support",
      link: "/get-support",
      page: "support",
      dropdown: [
        { name: "Maternity Mates", url: "/get-support/#Maternity-Mates" },
         {
          name: "Healing Conversations",
          url: "/get-support/#Healing-Conversations",
        },
        {
          name: "Her Health",
          url: "/get-support/#Her-Health",
        },
       
        // { name: "Peer Support Group", url: "/get-support/#Group-Peer-Support" },
        {
          name: "Conversation Cafe",
          url: "/get-support/#Conversation-Cafe",
        },
      ],
    },
    {
      name: "About",
      link: "/about-us",
      page: "about",
      dropdown: [
        { name: "About Sister Circle", url: "/about-us/#about-sister-circle" },
        { name: "Our vision", url: "/about-us/#our-vision" },
        { name: "Our mission", url: "/about-us/#our-mission" },
        { name: "Our values", url: "/about-us/#our-values" },
        { name: "The women we support", url: "/about-us/#the-women-we-support" },
        { name: "Our impact", url: "/about-us/#our-impact" },
        { name: "Our story", url: "/about-us/#our-story" },
        // { name: "Our team", url: "/about-us/#our-team" },
      ],
    },
    {
      name: "Volunteer with us",
      link: "/volunteer-with-us",
      page: "volunteer-with-us",
      dropdown: [
        { name: "Maternity Mates", url: "/volunteer-with-us/#maternity-mates" },
        { name: "Peer Support Volunteer", url: "/volunteer-with-us/#peer-support" },
        // { name: "Counselling", url: "/volunteer-with-us/#counselling" },
        
      ],
    },
    {
      name: "Get involved",
      link: "/get-involved",
      page: "get-involved",
      dropdown: [
        { name: "Partner with us", url: "/get-involved/#partner" },
        { name: "Donate", url: "/get-involved/#donate" },
        { name: "Fundraise", url: "/get-involved/#fundraise" },
      ],
    },
    { name: "Contact", link: "/contact-us", page: "contact" },
    // {
    //   name: "EN",
    //   dropdown: [
    //     { name: "English", url: "#" },
    //     { name: "বাংলা", url: "#" },
    //     { name: "Français", url: "#" },
    //     { name: "हिन्दी", url: "#" },
    //     { name: "lietuvių", url: "#" },
    //     { name: "Oluganda", url: "#" },
    //     { name: "Русский", url: "#" },
    //     { name: "Soomaali", url: "#" },
    //     { name: "kiswahili", url: "#" },
    //     { name: "українська", url: "#" },
    //     { name: "اردو", url: "#" },
    //   ],
    // },
    {
      name: "Donate",
      link: "https://donorbox.org/sister-circle",
      external: true,
    },

    {
      name: "Exit site",
      action: () => (window.location.href = "https://www.google.com"),
    },
  ], []); // Empty dependency array - menuItems never changes

  // ========================================
  // EVENT HANDLERS
  // ========================================
  
  /**
   * Language selector handler (for future localization feature)
   */
  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    // Logic to change the site's language will be implemented later
  };

  /**
   * Mouse enter handler for menu items
   * - Clears any pending hide timeout to keep dropdown open
   * - Prevents dropdown activation for items without dropdowns (Contact, Donate, Exit)
   * - Uses useCallback to prevent function recreation on every render
   */
  const handleMouseEnter = useCallback((index) => {
    if (hideDropdownTimeout.current) {
      clearTimeout(hideDropdownTimeout.current);
    }

    // Prevent overlay and ensure logo visibility for specific items
    if (
      menuItems[index].name === "Exit site" ||
      menuItems[index].name === "Contact" ||
      menuItems[index].name === "Donate"
    ) {
      setActiveDropdown(null); // Prevent dropdown activation
      setDropdownHeight(0); // Reset dropdown height
    } else {
      setActiveDropdown(index);
    }
  }, [menuItems]);

  /**
   * Mouse leave handler for menu items
   * - Delays hiding the dropdown by 500ms to allow user to move mouse to dropdown items
   * - Uses useCallback to prevent function recreation on every render
   */
  const handleMouseLeave = useCallback((index) => {
    hideDropdownTimeout.current = setTimeout(() => {
      if (activeDropdown === index) {
        setActiveDropdown(null);
      }
    }, 500); // Adjust the delay as needed
  }, [activeDropdown]);

  // ========================================
  // EFFECTS (Side Effects & Lifecycle)
  // ========================================
  
  /**
   * Effect: Measure and update dropdown height for smooth animations
   * Runs whenever activeDropdown changes
   */
  useEffect(() => {
    if (dropdownRef.current && activeDropdown !== null) {
      setDropdownHeight(dropdownRef.current.scrollHeight); // Measure dropdown height
    } else {
      setDropdownHeight(0); // Reset height when no dropdown is active
    }
  }, [activeDropdown]);

  /**
   * Effect: Handle responsive behavior on window resize
   * Closes mobile menu and dropdowns when transitioning to desktop view (>768px)
   */
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false); // Close mobile menu if screen width exceeds 768px
        setActiveDropdown(null); // Close any open submenu
      }
    };

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Call handleResize initially to set the correct state
    handleResize();

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /**
   * Toggle mobile menu visibility
   * Closes any open dropdowns when closing the mobile menu
   */
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => {
      if (prev) {
        setActiveDropdown(null); // Close any open submenu when closing the mobile menu
      }
      return !prev;
    });
  };

  /**
   * Effect: Page-load fade-in for desktop nav items
   * Waits for one animation frame + a short timeout so the browser has
   * finished laying out the flex items before making them visible.
   * This prevents the brief positional "shake" seen when navigating between pages.
   */
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      const timerId = setTimeout(() => setIsNavReady(true), 60);
      // Store timerId on the ref so cleanup can clear it
      navReadyTimer.current = timerId;
    });
    return () => {
      cancelAnimationFrame(rafId);
      if (navReadyTimer.current) clearTimeout(navReadyTimer.current);
    };
  }, []);

  /**
   * Effect: Handle scroll behavior
   * - When scrollY > 70px: Makes navbar sticky and shows logo
   * - When scrollY <= 70px: Returns to default state (collapsed on desktop)
   * Runs once on mount and attaches scroll listener
   */
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 70) {
        // Apply scroll behavior when user scrolls down
        setMenuTop(
          `mx-auto xs:fixed w-full z-[22] text-[#b24029] transition-all duration-1000 ease-in-out xs:h-auto bg-[#DCB1FA] md:bottom-auto xs:bottom-0 md:transform-gpu`
        );
        setLogoShow(
          "h-10 opacity-100 transition-opacity duration-1000 ease-in-out"
        );
      } else {
        // Revert to default behavior when at the top of the page
        setMenuTop(
          `mx-auto xs:fixed md:absolute w-full z-[22] text-[#b24029] transition-all duration-1000 ease-in-out md:h-0 xs:h-auto bg-[#DCB1FA] md:bottom-auto xs:bottom-0 md:transform-gpu`
        );
        setLogoShow("h-10 opacity-0 transition-opacity duration-1000 ease-in-out");
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  /**
   * Effect: Handle hover behavior at top of page
   * - Only active when scrollY <= 70px (scroll handler takes over when scrolled)
   * - Expands navbar and shows logo when hovering over items with dropdowns
   * - Uses ref comparison to prevent unnecessary state updates (performance optimization)
   * 
   * Performance note: prevNavStateRef prevents calling setMenuTop/setLogoShow
   * with the same value, which would cause unnecessary re-renders
   */
  useEffect(() => {
    // Only apply hover behavior when at the top of the page
    if (window.scrollY > 70) return; // Exit early if scrolled - scroll handler manages state
    
    let newMenuTop, newLogoShow;
    
    if (isNavHovered && activeDropdown !== null) {
      // Expand navbar and show logo on hover when at page top
      newMenuTop = `mx-auto xs:fixed md:absolute w-full z-[22] text-[#b24029] transition-all duration-1000 ease-in-out hover:h-auto bg-[#DCB1FA] md:bottom-auto xs:bottom-0 md:transform-gpu`;
      newLogoShow = "h-10 opacity-100 transition-opacity duration-1000 ease-in-out";
    } else if (!isNavHovered || activeDropdown === null) {
      // Collapse navbar and hide logo when not hovering
      newMenuTop = `mx-auto xs:fixed md:absolute w-full z-[22] text-[#b24029] transition-all duration-1000 ease-in-out md:h-0 xs:h-auto bg-[#DCB1FA] md:bottom-auto xs:bottom-0 md:transform-gpu`;
      newLogoShow = "h-10 opacity-0 transition-opacity duration-1000 ease-in-out";
    }
    
    // Only update state if values actually changed (prevents unnecessary re-renders)
    if (prevNavStateRef.current.menuTop !== newMenuTop) {
      setMenuTop(newMenuTop);
      prevNavStateRef.current.menuTop = newMenuTop;
    }
    if (prevNavStateRef.current.logoShow !== newLogoShow) {
      setLogoShow(newLogoShow);
      prevNavStateRef.current.logoShow = newLogoShow;
    }
  }, [isNavHovered, activeDropdown]);

  /**
   * Handle mouse entering the navbar container
   * Clears any pending timeout to keep navbar expanded
   */
  const handleNavMouseEnter = () => {
    // Clear any pending timeout when mouse enters
    if (navHoverTimeout.current) {
      clearTimeout(navHoverTimeout.current);
      navHoverTimeout.current = null;
    }
    setIsNavHovered(true);
  };

  /**
   * Handle mouse leaving the navbar container
   * Delays collapsing the navbar by 600ms to give user time to:
   * - Move mouse to dropdown items
   * - Move mouse back to navbar
   * The 600ms delay is longer than the dropdown hideTimeout (500ms) to prevent
   * premature collapse when mouse exits straight up/out of browser
   */
  const handleNavMouseLeave = () => {
    // Delay setting isNavHovered to false to give time for dropdown interaction
    // Using 600ms to be longer than the dropdown hideTimeout (500ms)
    navHoverTimeout.current = setTimeout(() => {
      setIsNavHovered(false);
    }, 600);
  };

  // ========================================
  // RENDER
  // ========================================
  
  return (
    <>
      {/* Black overlay */}
      {activeDropdown !== null && (
        <div
          className="fixed inset-0 xs:bg-none xs:bg-opacity-0  md:bg-black md:bg-opacity-40 z-[21] transition-opacity duration-1000 ease-in-out bg-tr "
          onMouseEnter={() => {
            setActiveDropdown(null);
            // Close dropdown when overlay is hovered
          }}
          // Reset logo opacity when leaving overlay
        ></div>
      )}

      <nav
        className={menuTop}
        style={{
          paddingBottom: dropdownHeight ? `${dropdownHeight}px` : "",
        }}
        onMouseEnter={() => {
          handleNavMouseEnter();
          // Close dropdown when overlay is hovered
        }}
        onMouseLeave={() => {
          handleNavMouseLeave(); // Added curly braces to wrap the function call
        }} // Reset logo opacity when leaving overlay
      >
        <div className=" md:flex xs:hidden  max-w-[1440px] mx-auto py-5 px-4 justify-between items-center antialiased">
          {/* Logo */}
          <a href="/">
            <img src={logoDesk} alt="sister circle logo" className={logoShow} />
          </a>

          <div className={`hidden md:flex md:items-center responsive-nav-gap transition-opacity duration-300 ease-in-out ${isNavReady ? 'opacity-100' : 'opacity-0'}`}>
            {menuItems.map((item, index) => (
              <div
                key={index}
                className="relative group flex items-center"
                onMouseEnter={() => handleMouseEnter(index)} // Keep dropdown open when hovering over parent
                onMouseLeave={() => handleMouseLeave(index)} // Close dropdown when leaving parent and submenu
              >
                {/* --- Clickable menu items with dropdown and navigation --- */}
                {item.link && item.dropdown && (
                  <button
                    type="button"
                    onClick={() => (window.location.href = item.link)}
                    className={`responsive-nav-padding py-2 rounded-[40.5px] responsive-nav-text ${
                      props.currentPage === item.page
                        ? "bg-[#b24029] bg-opacity-20"
                        : "hover:bg-[#b24029] hover:bg-opacity-20"
                    } ${
                      activeDropdown === index
                        ? "bg-[#b24029] bg-opacity-20"
                        : ""
                    }`}
                    onMouseEnter={() => handleMouseEnter(index)}
                  >
                    {item.name}
                  </button>
                )}

                {/* --- Contact link --- */}
                {item.link && item.name === "Contact" && !item.dropdown && (
                  <a
                    href={item.link}
                    className={`responsive-nav-padding py-[8px] rounded-[40.5px] responsive-nav-text ${
                      props.currentPage === item.page
                        ? "bg-[#b24029] bg-opacity-20"
                        : "hover:bg-[#b24029] hover:bg-opacity-20"
                    }`}
                  >
                    {item.name}
                  </a>
                )}

                {/* --- Donate button --- */}
                {item.link && item.name === "Donate" && (
                  <button
                    onClick={() => window.open(item.link, "_blank")}
                    className="flex items-center responsive-nav-padding py-[0.45rem] bg-[#b24029] text-[#F7E1DA] rounded-[40.5px] transition duration-1000 responsive-nav-text"
                  >
                    <img
                      src={heartIcon}
                      alt="heart Icon"
                      className="pr-[0.4rem] h-auto  xl:w-[1.386rem] x1200:w-[1.3rem]
                      lgx:w-[1.2rem] lg:w-[1.1rem] md:w-4 "
                    />
                    {item.name}
                  </button>
                )}

                {/* --- Exit site button --- */}
                {item.action && (
                  <button
                    onClick={item.action}
                    className="flex items-center responsive-nav-padding py-[0.45rem] bg-[#b24029] text-[#F7E1DA] rounded-[40.5px] responsive-nav-text"
                  >
                    {item.name}
                    <img
                      src={exitIconM}
                      alt="Exit Icon"
                      className="pl-[0.2rem] h-auto xl:w-6 lgx:w-[22px] lg:w-5 md:w-4 "
                    />
                  </button>
                )}

                {/* --- Dropdown button for items without link --- */}
                {!item.link && !item.action && (
                  <button
                    className={`responsive-nav-padding py-2 hover:bg-[#b24029] hover:bg-opacity-20 rounded-[40.5px] responsive-nav-text ${
                      activeDropdown === index
                        ? "bg-[#b24029] bg-opacity-20"
                        : ""
                    }`}
                    onMouseEnter={() => handleMouseEnter(index)}
                  >
                    {item.name}
                  </button>
                )}

                {/* --- Dropdown menu --- */}
                {item.dropdown && (
                  <div
                    ref={activeDropdown === index ? dropdownRef : null}
                    className={`
                        absolute left-0 top-full text-[#b24029] overflow-hidden 
                        transition-opacity ease-in-out 
                        ${
                          activeDropdown === index
                            ? "duration-[1500ms] opacity-100 visible"
                            : "duration-[500ms] opacity-0 invisible"
                        } 
                       responsive-nav-text
                      `}
                    onMouseEnter={() => handleMouseEnter(index)}
                  >
                    <div className="  lg:pt-8  xs:pt-6 lg:pb-4  xs:pb-2">
                      {item.name === "EN"
                        ? item.dropdown.map((language, subIndex) => (
                            <button
                              key={subIndex}
                              onClick={() =>
                                handleLanguageChange(language.name)
                              }
                              className={` whitespace-nowrap text-left  block px-4  py-1  hover:underline  hover:decoration-[1.4px] hover:underline-offset-[3px] transition duration-100 ${
                                selectedLanguage === language.name
                                  ? "font-bold"
                                  : ""
                              }`}
                            >
                              {language.name}
                            </button>
                          ))
                        : item.dropdown.map((submenu, subIndex) => (
                            <a
                              key={subIndex}
                              href={submenu.url}
                              className=" whitespace-nowrap text-left block px-4 py-1 hover:underline hover:decoration-[1.4px] hover:underline-offset-[3px] transition duration-100 "
                            >
                              {submenu.name}
                            </a>
                          ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ==========================================Mobile Menu =================== */}

        <div
          className={`md:hidden  bg-[#DCB1FA]  w-full overflow-y-auto ${
            isMobileMenuOpen ? "h-[100vh] pb-28 fixed top-0" : "h-fit"
          } `}
        >
          <div className={` xs:fixed md:absolute bottom-0 w-full bg-[#DCB1FA] flex items-center justify-between s:px-7 xs:px-3 py-3 h-[100px] z-[1] transition-opacity duration-300 ease-in-out ${isNavReady ? 'opacity-100' : 'opacity-0'} `}>
            {/* Background extension for Chrome iOS - covers gap when browser toolbar collapses */}
            <div className="md:hidden absolute left-0 right-0 top-0 bg-[#DCB1FA] pointer-events-none" style={{ bottom: '-50px', zIndex: -1 }} />
            <div
              className="md:hidden flex items-center cursor-pointer"
              onClick={toggleMobileMenu}
            >
              <svg
                className="min-[465px]:w-16 s:w-12 xs:w-10 min-[465px]:h-[3.563rem] s:h-12 xs:h-10"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  className={`transition-transform duration-300 ${
                    isMobileMenuOpen
                      ? "rotate-45 translate-y-0 translate-x-2"
                      : ""
                  }`}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.2"
                  d="M4 7h16"
                />
                <path
                  className={`transition-opacity duration-300 ${
                    isMobileMenuOpen ? "opacity-0" : "opacity-100"
                  }`}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.2"
                  d="M4 12h16"
                />
                <path
                  className={`transition-transform duration-300 ${
                    isMobileMenuOpen
                      ? "-rotate-45 translate-y-[0.6rem] -translate-x-[0.6rem]"
                      : ""
                  }`}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.2"
                  d="M4 17h16"
                />
              </svg>
            </div>
            <div
              className="flex
            gap-[18px] h-[45px]"
            >
              <button
                onClick={() => window.open(menuItems[5].link, "_blank")}
                className=" flex items-center  border border-[#b24029] min-[465px]:px-[1.642rem] s:px-[1.4rem] min-[350px]:px-[1.25rem] xs:px-4  rounded-[40.5px] min-[465px]:text-2xl s:text-xl min-[350px]:text-lg xs:text-base text-[#FFE1D9] bg-[#b24029] hover:border-collapse antialiased"
              >
                <img
                  src={heartIcon} // Conditional image
                  alt="heart Icon"
                  className="pr-[0.4rem] h-auto  min-[465px]:w-[1.586rem]  s:w-[1.386rem] xs:w-[1.2rem]"
                />
                Donate
              </button>
              <button
                onClick={() => (window.location.href = "https://www.google.com")}
                className="flex items-center border border-[#b24029] min-[465px]:px-[1.642rem] s:px-[1.4rem] min-[350px]:px-[1.25rem] xs:px-4   rounded-[40.5px] min-[465px]:text-2xl  s:text-xl min-[350px]:text-lg xs:text-base text-[#FFE1D9] bg-[#b24029] hover:border-collapse antialiased"
              >
                Exit site
                <img
                  src={exitIconM} // Conditional image
                  alt="Exit Icon"
                  className="pl-[0.4rem] h-auto  min-[465px]:w-[1.9rem] s:w-[1.6rem] xs:w-[1.4rem]"
                />
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <>
              <div
                className=" mb-[54px] mx-7"
                style={{
                  marginTop: `calc(33px + env(safe-area-inset-top))`, // Adjust padding dynamically
                }}
              >
                <a href="/">
                  <img src={logoDesk} alt="Logo" className="h-[49px]" />
                </a>
              </div>

              {menuItems
                .filter((item) => item.name !== "Exit site") // Exclude "Exit site" from mobile menu
                .filter((item) => item.name !== "Donate") // Exclude "Donate" from mobile menu
                .map((item, index) => (
                  <div key={index} className="">
                    <div className="flex  gap-[18px] px-9 py-[0.65rem]">
                      <a
                        href={item.link || "https://google.com"}
                        target={item.external ? "_blank" : "_self"}
                        rel={item.external ? "noopener noreferrer" : undefined}
                        className="text-[#b24029] text-[35px] leading-[39px] antialiased"
                      >
                        {item.name}
                      </a>
                      {item.dropdown && (
                        <button
                          onClick={() =>
                            setActiveDropdown(
                              activeDropdown === index ? null : index
                            )
                          }
                          className="text-[#b24029] text-2xl  relative top-[3px] flex items-center"
                        >
                          {activeDropdown === index ? (
                            // <IoIosArrowUp />
                            <img
                              src={arrowUp}
                              alt="Arrow Up"
                              className="w-4 h-auto"
                            />
                          ) : (
                            // <IoIosArrowDown />
                            <img
                              src={arrowDown}
                              alt="Arrow Down"
                              className="w-4 h-auto"
                            />
                          )}
                        </button>
                      )}
                    </div>
                    {item.dropdown && (
                      <div
                        className={`pl-12 overflow-hidden transition-[max-height] duration-500 ease-in-out ${
                          activeDropdown === index ? "max-h-[600px]" : "max-h-0"
                        }`}
                      >
                        {item.dropdown.map((submenu, subIndex) => (
                          <a
                            key={subIndex}
                            href={submenu.url}
                            onClick={() => {
                              setIsMobileMenuOpen(false);
                              setActiveDropdown(null);
                            }}
                            className="block text-[#b24029] text-[25px] leading-[33px] antialiased  py-[11px] hover:underline"
                          >
                            {submenu.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
